import { FGCheckboxInput, FGTextInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { PedigreePrefixApi, SavePedigreePrefixDto } from "../../../../../api";
import { SmallFormGenerator } from "../../../../../components";
import { useApiService, useTl, useSaveApi } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";

export interface IPedigreePrefixPanelProps {
  countryId: number;
  onSaved: () => void;
}

export const PedigreePrefixPanel: React.FunctionComponent<IPedigreePrefixPanelProps> = ({ countryId, onSaved }) => {
  const { t } = useTl();
  const api = useApiService(PedigreePrefixApi);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      prefix: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("unique-prefix", t(ETLCodes.PrefixAlreadyExists), async function (value) {
          if (value) {
            const error = await api.isPrefixUnique(countryId, value);
            return error.value;
          }
          return true;
        }),
    });
  }, [api, countryId, t]);

  const { saving, saveItem } = useSaveApi<SavePedigreePrefixDto>(
    React.useMemo(
      () => ({
        saveApiFn: (data) => api.saveNewPedigreePrefix(data),
        onSaveFn: () => onSaved(),
      }),
      [api, onSaved]
    )
  );

  const initialData = React.useMemo(() => new SavePedigreePrefixDto({ countryId: countryId }).toJSON(), [countryId]);

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      saving={saving}
      initialValues={initialData}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={110}
    >
      <FGTextInput name="prefix" label={t(ETLCodes.Prefix)} />
      <FGCheckboxInput name="fciRecognized" label={t(ETLCodes.FciRecognized)} />
    </SmallFormGenerator>
  );
};
